import clsx from 'clsx';
import { FaCircleCheck } from 'react-icons/fa6';
import { FiEdit2, FiKey } from 'react-icons/fi';
import { GoAlertFill } from 'react-icons/go';
import { Link } from 'react-router-dom';

import { Button } from '@/components/button/button';
import { Icon } from '@/components/icon/icon';
import { Heading } from '@/components/typography/heading';
import { Contract, DataContractYAML } from '@/types/dataContracts';
import { dataPlatformSecretLinkPath } from '@/utils/secretsLink';
import { getContractSecretName } from '@/utils/yamlSecretEdit';

import contractStyles from '../../contractDetails.module.scss';
import styles from './CredentialsSection.module.scss';

type Props = {
  contract: Contract;
  parsedYAML: DataContractYAML | null;
  canEdit: boolean;
  onEdit: () => void;
};

export function CredentialsSection( { contract, parsedYAML, canEdit, onEdit }: Props ) {
  // DCM persists secret_name on the contract, but fall back to YAML for older
  // contracts that pre-date the indexed column.
  const secretName = contract.secret_name
    || getContractSecretName( contract.yaml_content )
    || ( parsedYAML?.servers?.source?.secrets?.secret_name ?? null );

  const secretType = contract.secret_type
    || parsedYAML?.servers?.source?.secrets?.type
    || null;

  const error = contract.secret_error?.trim() ? contract.secret_error : null;

  return (
    <div className={ clsx( 'layout__row', 'pad' ) }>
      <div className={ clsx( contractStyles.card, contractStyles.noBorderCard ) } data-testid='credentials-card'>
        <div className={ contractStyles.cardHeader }>
          <Heading as='h3' size='sm'>Credentials</Heading>
          { canEdit && secretName && (
            <Button
              variant='secondary'
              size='sm'
              onClick={ onEdit }
              icon={ <Icon icon={ FiEdit2 } /> }
              data-testid='credentials-edit-button'
            >
              Edit credential
            </Button>
          ) }
        </div>
        <div className={ contractStyles.cardBody }>
          { secretName ? (
            <div className={ styles.row } data-testid='credentials-linked'>
              <div className={ styles.secretInfo }>
                <Icon icon={ FiKey } className={ styles.secretIcon } />
                <Link
                  to={ dataPlatformSecretLinkPath( secretName ) }
                  className={ styles.secretName }
                  data-testid='credentials-secret-name'
                >
                  { secretName }
                </Link>
                { secretType && (
                  <span className={ styles.typeChip } data-testid='credentials-secret-type'>
                    { secretType }
                  </span>
                ) }
              </div>
              <div className={ styles.statusContainer }>
                { error ? (
                  <div className={ clsx( styles.statusBadge, styles.statusError ) } data-testid='credentials-status-error'>
                    <Icon icon={ GoAlertFill } />
                    <span>{ error }</span>
                  </div>
                ) : (
                  <div className={ clsx( styles.statusBadge, styles.statusOk ) } data-testid='credentials-status-ok'>
                    <Icon icon={ FaCircleCheck } />
                    <span>Linked</span>
                  </div>
                ) }
              </div>
            </div>
          ) : (
            <div className={ styles.emptyState } data-testid='credentials-empty'>
              <p className={ styles.emptyText }>No credential linked to this contract.</p>
              { canEdit && (
                <Button
                  variant='primary'
                  size='sm'
                  onClick={ onEdit }
                  icon={ <Icon icon={ FiKey } /> }
                  data-testid='credentials-link-button'
                >
                  Link credential
                </Button>
              ) }
            </div>
          ) }
        </div>
      </div>
    </div>
  );
}
